import * as React from "react";
import {
  Card, CardHeader, List, ListItem, Typography, Box, Pagination, Checkbox,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { ButtonBrand, gold, fontBrand } from "../../../../getLPTheme";

type Item = {
  user: string;
  name: string;
  lastname: string;
  email: string;
  phone: string;
  role: string;
  date: string | Date;
  status: "active" | "inactive" | string;
  paymentStatus?: string;
  subscription?: any | null;
};

const PAGE_SIZE = 9;

// sm: check | nombre | apellido | email | estado | suscripción
const COLS_SM =
  "46px minmax(160px,1fr) minmax(160px,1fr) minmax(260px,1.5fr) 140px 160px";

// md: + teléfono + rol
const COLS_MD =
  "46px minmax(160px,1fr) minmax(160px,1fr) minmax(300px,1.6fr) 160px 140px 140px 160px";

// lg: + fecha
const COLS_LG =
  "46px minmax(180px,1fr) minmax(180px,1fr) minmax(340px,1.8fr) 180px 160px 160px 140px 160px";

const GRID_GAP = 0;
const PAD_X = 4;

export default function Tabla({
  items,
  onActivate,
  onInactivate,
}: {
  items: Item[];
  onActivate: (ids: string[]) => Promise<void> | void;
  onInactivate: (ids: string[]) => Promise<void> | void;
}) {
  const rows = items.map((x) => ({
    id: x.user,
    name: x.name,
    lastname: x.lastname,
    email: x.email,
    phone: x.phone,
    role: x.role,
    date: new Date(x.date),
    status: x.status,
    paymentStatus: (x as any).paymentStatus,
    subscription: x.subscription ?? null,
  }));

  const [page, setPage] = React.useState(1);
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);
  const pages = Math.ceil(rows.length / PAGE_SIZE);

  // selección
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const selectedIds = Array.from(selected);
  const hasSelection = selectedIds.length > 0;

  const handleActivate = () => onActivate(selectedIds);
  const handleInactivate = () => onInactivate(selectedIds);

  // Pop-up suscripción
  const [openSubDialog, setOpenSubDialog] = React.useState(false);
  const [subDialogTitle, setSubDialogTitle] = React.useState("");
  const [subDialogJson, setSubDialogJson] = React.useState<any>(null);

  const openSubscriptionDialog = (userId: string, subscription: any) => {
    setSubDialogTitle(`Suscripción - ${userId}`);
    setSubDialogJson(subscription);
    setOpenSubDialog(true);
  };

  const closeSubscriptionDialog = () => {
    setOpenSubDialog(false);
    setSubDialogJson(null);
    setSubDialogTitle("");
  };

  const allCheckedOnPage =
    pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const someCheckedOnPage =
    pageRows.some((r) => selected.has(r.id)) && !allCheckedOnPage;

  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allCheckedOnPage) pageRows.forEach((r) => n.delete(r.id));
      else pageRows.forEach((r) => n.add(r.id));
      return n;
    });
  };

  const fmtDate = (d: Date) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

  const iconStatus = (s: string) =>
    s === "active" ? (
      <CheckCircleIcon fontSize="small" sx={{ color: "#28a745" }} />
    ) : (
      <CancelIcon fontSize="small" sx={{ color: "#dc3545" }} />
    );

  // suscripción
  const subscriptionLabel = (paymentStatus?: string) =>
    (paymentStatus || "").replace(/^Usuario\s+/i, "").trim().toLowerCase();

  const iconSubscription = (paymentStatus?: string) => {
    const label = subscriptionLabel(paymentStatus);
    return label === "habilitado" ? (
      <CheckCircleIcon fontSize="small" sx={{ color: "#28a745" }} />
    ) : (
      <CancelIcon fontSize="small" sx={{ color: "#dc3545" }} />
    );
  };

  const show = {
    sm: { display: { xs: "none", sm: "block" } },
    md: { display: { xs: "none", md: "block" } },
    lg: { display: { xs: "none", lg: "block" } },
  };

  return (
    <>
      <Card
        sx={{
          bgcolor: "#0B0F14",
          color: "#EAECEF",
          borderRadius: 3,
          border: "1px solid #2B3139",
          overflowX: "visible",
          fontFamily: fontBrand.principal,
        }}
      >
        <CardHeader
          title="Usuarios"
          sx={{
            "& .MuiCardHeader-title": { fontWeight: 600, fontSize: { xs: 18, sm: 20 } },
            px: 2,
            py: 1.5,
            bgcolor: "#0B0F14",
            borderBottom: "1px solid #2B3139",
          }}
        />

        {/* Acciones */}
        <Box sx={{ px: 2, pt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <ButtonBrand
            disabled={!hasSelection}
            onClick={handleInactivate}
            sx={{ flex: { xs: "1 1 48%", md: "1 1 32%" } }}
          >
            Inactivar
          </ButtonBrand>

          <ButtonBrand
            disabled={!hasSelection}
            onClick={handleActivate}
            sx={{ flex: { xs: "1 1 48%", md: "1 1 32%" } }}
          >
            Activar
          </ButtonBrand>
        </Box>

        <Divider sx={{ borderColor: "#2B3139", mt: 2 }} />

        {/* =======================
            MOBILE (xs): cards 100% ancho, sin scroll horizontal
           ======================= */}
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <List disablePadding sx={{ width: "100%" }}>
            {pageRows.map((r) => {
              const isChecked = selected.has(r.id);
              const subLabel = subscriptionLabel(r.paymentStatus) || "—";

              return (
                <ListItem
                  key={r.id}
                  sx={{
                    width: "100%",
                    display: "block",
                    px: PAD_X,
                    py: 1.25,
                    borderBottom: "1px solid #2B3139",
                    "&:hover": { bgcolor: "#0E141B" },
                    boxSizing: "border-box",
                  }}
                >
                  {/* Header: checkbox + nombre */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Checkbox
                      checked={isChecked}
                      onChange={() => toggleSelect(r.id)}
                      sx={{ p: 0, color: "#5E6775", mt: 0.25 }}
                    />

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {r.name} {r.lastname}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.85,
                          display: "block",
                          mt: 0.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={r.email}
                      >
                        {r.email}
                      </Typography>

                      <Typography variant="caption" sx={{ opacity: 0.75, display: "block", mt: 0.25 }}>
                        {r.phone}
                      </Typography>

                      {/* Estado + Suscripción */}
                      <Box sx={{ display: "flex", gap: 2, mt: 0.75, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {iconStatus(r.status)}
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {r.status === "active" ? "Activo" : "Inactivo"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {iconSubscription(r.paymentStatus)}
                          {r.subscription ? (
                            <Typography
                              component="button"
                              onClick={() => openSubscriptionDialog(r.id, r.subscription)}
                              style={{
                                background: "transparent",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                font: "inherit",
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: gold[300],
                                  fontWeight: 800,
                                  textDecoration: "underline",
                                  textUnderlineOffset: "3px",
                                }}
                              >
                                {subLabel}
                              </Box>
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {subLabel}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* =======================
            DESKTOP (sm+): tabla con scroll horizontal
           ======================= */}
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Box sx={{ overflowX: "auto", width: "100%", maxWidth: "100%", bgcolor: "#0B0F14" }}>
            <Box sx={{ minWidth: "max-content", display: "inline-block" }}>
              {/* Header (sm+) */}
              <Box
                sx={{
                  width: "max-content",
                  display: "grid",
                  gridTemplateColumns: { sm: COLS_SM, md: COLS_MD, lg: COLS_LG },
                  columnGap: GRID_GAP,
                  px: PAD_X,
                  py: 1,
                  alignItems: "center",
                  fontSize: 12,
                  opacity: 0.7,
                  borderBottom: "1px solid #2B3139",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  bgcolor: "#0B0F14",
                  "& > *": { whiteSpace: "nowrap", textAlign: "center" },
                }}
              >
                <Checkbox
                  checked={allCheckedOnPage}
                  indeterminate={someCheckedOnPage}
                  onChange={toggleSelectAllOnPage}
                  sx={{ p: 0, color: "#5E6775" }}
                />
                <Typography sx={show.sm}>Nombre</Typography>
                <Typography sx={show.sm}>Apellido</Typography>
                <Typography sx={show.sm}>Email</Typography>
                <Typography sx={show.sm}>Estado</Typography>
                <Typography sx={show.sm}>Suscripción</Typography>

                <Typography sx={show.md}>Teléfono</Typography>
                <Typography sx={show.md}>Rol</Typography>

                <Typography sx={show.lg}>Fecha Alta</Typography>
              </Box>

              {/* Filas (sm+) */}
              <List disablePadding sx={{ width: "max-content" }}>
                {pageRows.map((r) => {
                  const isChecked = selected.has(r.id);
                  return (
                    <ListItem
                      key={r.id}
                      sx={{
                        width: "max-content",
                        display: "grid",
                        gridTemplateColumns: { sm: COLS_SM, md: COLS_MD, lg: COLS_LG },
                        columnGap: GRID_GAP,
                        alignItems: "center",
                        px: PAD_X,
                        py: 1.25,
                        borderBottom: "1px solid #2B3139",
                        "&:hover": { bgcolor: "#0E141B" },
                        boxSizing: "border-box",
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggleSelect(r.id)}
                        sx={{ p: 0, color: "#5E6775" }}
                      />

                      <Typography sx={{ ...show.sm, textAlign: "center" }}>{r.name}</Typography>
                      <Typography sx={{ ...show.sm, textAlign: "center" }}>{r.lastname}</Typography>

                      <Typography
                        sx={{
                          ...show.sm,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 420,
                        }}
                        title={r.email}
                      >
                        {r.email}
                      </Typography>

                      <Box
                        sx={{
                          ...show.sm,
                          display: { xs: "none", sm: "flex" },
                          gap: 1,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {iconStatus(r.status)}
                        <Typography>{r.status === "active" ? "Activo" : "Inactivo"}</Typography>
                      </Box>

                      <Box
                        sx={{
                          ...show.sm,
                          display: { xs: "none", sm: "flex" },
                          gap: 1,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {iconSubscription(r.paymentStatus)}
                        {r.subscription ? (
                          <Typography
                            component="button"
                            onClick={() => openSubscriptionDialog(r.id, r.subscription)}
                            style={{
                              background: "transparent",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              font: "inherit",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: gold[300],
                                fontWeight: 700,
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                              }}
                            >
                              {subscriptionLabel(r.paymentStatus) || "—"}
                            </Box>
                          </Typography>
                        ) : (
                          <Typography>{subscriptionLabel(r.paymentStatus) || "—"}</Typography>
                        )}
                      </Box>

                      <Typography sx={{ ...show.md, textAlign: "center" }}>{r.phone}</Typography>
                      <Typography sx={{ ...show.md, textAlign: "center" }}>{r.role}</Typography>
                      <Typography sx={{ ...show.lg, textAlign: "center" }}>{fmtDate(r.date)}</Typography>
                    </ListItem>
                  );
                })}
              </List>

              {/* Paginación */}
              <Box
                sx={{
                  width: "max-content",
                  ml: "auto",
                  px: 1.5,
                  py: 1.25,
                  borderTop: "1px solid #2B3139",
                }}
              >
                <Pagination
                  count={pages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      bgcolor: "#0B0F14",
                      border: "1px solid #2B3139",
                      color: "white",
                    },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      bgcolor: gold[500],
                      color: "#0B0F14",
                      borderColor: gold[500],
                      "&:hover": { bgcolor: gold[500] },
                    },
                    "& .MuiPaginationItem-previousNext": { color: "white" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Dialog suscripción */}
      <Dialog
        open={openSubDialog}
        onClose={closeSubscriptionDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#0B0F14",
            color: "white",
            border: "1px solid #2B3139",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: gold[500], fontWeight: 800 }}>
          {subDialogTitle}
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: "#2B3139" }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 12,
              lineHeight: 1.4,
              color: "white",
            }}
          >
            {subDialogJson ? JSON.stringify(subDialogJson, null, 2) : ""}
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #2B3139" }}>
          <Button onClick={closeSubscriptionDialog} sx={{ color: "white" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
